import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import useResponsiveWithHydrationFix from "../../hooks/useResponsiveWithHydrationFix";

import Banner from "../blocks/Banner";
import BrowseProducts from "../blocks/BrowseProducts";
import CollectionTile from "../blocks/CollectionTile";
import CollectionTiles from "../blocks/CollectionTiles";
import CompanyReviews from "../blocks/CompanyReviews";
import FullScreenVideo from "../blocks/FullScreenVideo";
import LPImage from "../blocks/LPImage";
import LPSimpleBloc from "../blocks/LPSimpleBloc";
import LPText from "../blocks/LPText";
import LPSpacing from "../blocks/LPSpacing";
import MediaLogosBanner from "../blocks/MediaLogosBanner";
import PopularCategories from "../blocks/PopularCategories";
import ProductsCarousel from "../blocks/ProductsCarousel";
import ScrollableImages from "../blocks/ScrollableImages";
import TextImage from "../blocks/TextImage";
import TextAndImages from "../blocks/TextAndImages";
import UgcVideos from "../blocks/UgcVideos";
import UspsBlock from "../blocks/UspsBlock";
import IframeBlock from "../blocks/IframeBlock";
import LPEmptyDiv from "../blocks/LPEmptyDiv";
import ProductRecommendations from "../blocks/ProductRecommendations";
import ProductRecommendationsPopup from "../ProductRecommendationsPopup";

function PopularCollectionsHydrationFix({ content, componentType }) {
  return useResponsiveWithHydrationFix(
    null,
    <PopularCategories content={content} componentType={componentType} />
  );
}

const CONTENTFUL_BLOCK_TYPES = {
  BANNER: "ContentfulLandingPageBanner",
  BROWSE_PRODUCTS: "ContentfulHomePageBrowseProducts",
  COLLECTION_TILES: "ContentfulLandingPageCollectionTiles",
  FULL_SCREEN_VIDEO: "ContentfulLandingPageFullScreenVideo",
  LP_IMAGE: "ContentfulLpImage",
  LP_SIMPLE_TEXT_CTA: "ContentfulLpSimpleTextCta",
  LP_TEXT: "ContentfulLpText",
  POPULAR_COLLECTIONS: "ContentfulHomePagePopularCollections",
  PRODUCTS_CAROUSEL: "ContentfulLandingPageProductsCarousel",
  REVIEWS: "ContentfulHomePageReviews",
  SCROLLABLE_IMAGES: "ContentfulLandingPageScrollableImages",
  TILE_CATEGORIES: "ContentfulHomePageTileCategories",
  TEXT_IMAGE: "ContentfulLandingPageTextAndImageVideo",
  TEXT_AND_IMAGES: "ContentfulLandingPageTextAndImages",
  UGC_VIDEOS: "ContentfulLandingPageVideoGrid",
  USPS_BLOCK: "ContentfulLandingPageUspsBlock",
  SPACING: "ContentfulLandingPageSpacing",
  MEDIA_LOGOS: "ContentfulLandingPageMediaLogos",
  IFRAME: "ContentfulLandingPageIframe",
  EMPTY_DIV: "ContentfulLandingPageEmptyDiv",
  PRODUCT_RECOMMENDATIONS: "ContentfulLandingPageProductRecommendations",
};

function ALCustomLandingPage({
  data,
  componentType,
  showAnchor = false,
  showTitleLpText = false,
  className = null,
  showSEOLinks = false,
}) {
  return data ? (
    <div className={className && className}>
      {data.blocks?.map((c, i) => {
        const typename = c?.__typename;
        if (typename) {
          switch (typename) {
            case CONTENTFUL_BLOCK_TYPES.SPACING:
              return <LPSpacing key={`customLandingPageSpacing_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.MEDIA_LOGOS:
              return <MediaLogosBanner key={`customLandingMediaLogosBanner_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.BANNER:
              return <Banner key={`customLandingBanner_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.BROWSE_PRODUCTS:
              return (
                <BrowseProducts
                  key={`customLandingPageBrowseProducts_${i}`}
                  type="hp"
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.COLLECTION_TILES:
              return (
                <CollectionTiles
                  key={`customLandingPageCollectionTiles_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.LP_IMAGE:
              return <LPImage key={`customLandingPageLPImage_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.LP_SIMPLE_TEXT_CTA:
              return (
                <LPSimpleBloc
                  key={`customLandingPageLPSimpleBlock_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.LP_TEXT:
              return (
                <LPText
                  key={`customLandingPageLPText_${i}`}
                  data={c}
                  showAnchor={showAnchor}
                  showTitle={showTitleLpText}
                  showSEOLinks={showSEOLinks}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.POPULAR_COLLECTIONS:
              return (
                <PopularCollectionsHydrationFix
                  key={`customLandingPagePopularCollections_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.PRODUCTS_CAROUSEL:
              return (
                <ProductsCarousel
                  key={`customLandingPageProductsCarousel_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.REVIEWS:
              return <CompanyReviews key={`customLandingPageCompanyReviews_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.SCROLLABLE_IMAGES:
              return (
                <ScrollableImages
                  key={`customLandingPageScrollableImages_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.TILE_CATEGORIES:
              return <CollectionTile key={`customLandingPageCollectionTile_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.TEXT_IMAGE:
              return <TextImage key={`customLandingPageTextImage_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.TEXT_AND_IMAGES:
              return <TextAndImages key={`customLandingPageTextAndImages_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.USPS_BLOCK:
              return <UspsBlock key={`customLandingPageUspsBlock_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.UGC_VIDEOS:
              return (
                <UgcVideos
                  key={`customLandingPageUgcVideos_${i}`}
                  content={c}
                  componentType={componentType}
                />
              );
            case CONTENTFUL_BLOCK_TYPES.FULL_SCREEN_VIDEO:
              return <FullScreenVideo key={`customLandingPageFullScreenvideo_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.IFRAME:
              return <IframeBlock key={`customLandingPageIframeBlock_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.EMPTY_DIV:
              return <LPEmptyDiv key={`customLandingPageEmptyDiv_${i}`} content={c} />;
            case CONTENTFUL_BLOCK_TYPES.PRODUCT_RECOMMENDATIONS:
              return (
                <>
                  <ProductRecommendations
                    key={`customLandingPageProductRecommendations_${i}`}
                    content={c}
                  />
                  <ProductRecommendationsPopup />
                </>
              );
            default:
              return null;
          }
        }
        return null;
      })}
    </div>
  ) : null;
}

export default withALErrorBoundary({
  name: "ALCustomLandingPage",
  priority: "P2",
})(ALCustomLandingPage);
